import React from "react";
import { useTranslation } from "react-i18next";
import type { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";
import type { TeamMember } from "../../Booking-Form/typings/team-member";
import {
  dashboard2SectionFieldsetLegendIconSx,
  dashboard2ManageColleaguesLinkSx
} from "../dashboard2-status.styles";
import { colleagueDisplayName } from "../utils/dashboard2-schedule.utils";
import {
  colleagueStatusActionLine,
  colleagueStatusPlaceLine,
  colleagueStatusTimeLine,
  statusAccentColor
} from "../utils/dashboard2-status-display.utils";
import { Dashboard2ColleagueAvatar } from "./Dashboard2ColleagueAvatar";
import { Dashboard2SectionPanel } from "./Dashboard2SectionPanel";
import { Dashboard2TodayColleaguesStripLoading } from "./Dashboard2SchedulesLoadingSkeleton";
import { useDashboard2VisibleColleaguesToday } from "../hooks/useDashboard2VisibleColleaguesToday";
import { Groups } from "@mui/icons-material";
import { Box, Link as MuiLink, Tooltip, Typography, useTheme } from "@mui/material";
import {
  DASHBOARD2_CHIP_AVATAR_SIZE_PX,
  dashboard2TodayColleagueBookingRowSx,
  dashboard2TodayColleagueIdentityRowSx,
  dashboard2TodayColleagueNameGroupSx,
  dashboard2TodayColleagueNarrowRowSx,
  dashboard2TodayColleagueRowSx,
  dashboard2TodayColleaguesPanelSx,
  dashboard2TodayColleaguesScrollSx,
  dashboard2TodayColleaguesSectionRootSx,
  dashboard2TodayStripSx
} from "../dashboard2.styles";

const COLLEAGUE_AVATAR_GAP_PX = 6;

type Dashboard2TodayColleaguesStripProps = {
  schedules: BookingScheduleInterface[];
  storedColleagues: TeamMember[];
  isLoading?: boolean;
  /** Wide top row scrolls inside the grid. Narrow lists show at most four colleagues. */
  isWideRow?: boolean;
  onShowColleaguesField?: () => void;
};

export const Dashboard2TodayColleaguesStrip: React.FC<Dashboard2TodayColleaguesStripProps> = ({
  schedules,
  storedColleagues,
  isLoading = false,
  isWideRow = false,
  onShowColleaguesField
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const visibleColleagues = useDashboard2VisibleColleaguesToday(schedules, storedColleagues);

  const hasStoredColleagues = storedColleagues.length > 0;

  const stripContent = (() => {
    if (isLoading) {
      return (
        <Dashboard2TodayColleaguesStripLoading
          rowCount={hasStoredColleagues ? Math.min(storedColleagues.length, 3) : 2}
        />
      );
    }

    if (!hasStoredColleagues) {
      return (
        <Box sx={dashboard2TodayStripSx} data-testid="dashboard2-today-colleagues-empty">
          <Typography variant="body2" color="text.secondary">
            {t("_dashboard2_noStoredColleaguesHint")}
          </Typography>
          {onShowColleaguesField ? (
            <MuiLink
              component="button"
              type="button"
              variant="inherit"
              onClick={onShowColleaguesField}
              aria-label={t("_dashboard2_showColleaguesFieldLinkAria")}
              sx={dashboard2ManageColleaguesLinkSx(theme)}
              data-testid="dashboard2-show-colleagues-field-link"
            >
              {t("_dashboard2_showColleaguesFieldLink")}
            </MuiLink>
          ) : null}
        </Box>
      );
    }

    if (visibleColleagues.length === 0) {
      return (
        <Box sx={dashboard2TodayStripSx} data-testid="dashboard2-today-no-colleagues-booked">
          <Typography variant="body2" color="text.secondary">
            {t("_dashboard2_noColleagueBookingsToday")}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={dashboard2TodayStripSx}>
        {visibleColleagues.map(({ colleague, status }) => {
          const statusLabel = colleagueStatusActionLine(status, t);
          const placeLine = colleagueStatusPlaceLine(status);
          const timeLine = colleagueStatusTimeLine(status);
          const displayName = colleagueDisplayName(colleague);

          return (
            <Box
              key={colleague.userId}
              sx={isWideRow ? dashboard2TodayColleagueRowSx : dashboard2TodayColleagueNarrowRowSx}
              data-testid={`dashboard2-today-colleague-row-${colleague.userId}`}
            >
              <Box sx={dashboard2TodayColleagueIdentityRowSx}>
                <Dashboard2ColleagueAvatar colleague={colleague} />
                <Box sx={dashboard2TodayColleagueNameGroupSx}>
                  <Tooltip title={displayName} placement="top" enterDelay={500}>
                    <Typography
                      variant="body2"
                      color="text.primary"
                      noWrap
                      sx={{ fontWeight: 500, minWidth: 0 }}
                    >
                      {displayName}
                    </Typography>
                  </Tooltip>
                  {timeLine ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      sx={{ flexShrink: 0 }}
                      data-testid={`dashboard2-today-colleague-time-${colleague.userId}`}
                    >
                      {timeLine}
                    </Typography>
                  ) : null}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    flexShrink: 0,
                    minWidth: 0
                  }}
                >
                  <Box
                    component="span"
                    aria-hidden
                    sx={{
                      width: 8,
                      height: 8,
                      flexShrink: 0,
                      borderRadius: "50%",
                      bgcolor: statusAccentColor(theme, status.kind)
                    }}
                  />
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {statusLabel}
                  </Typography>
                </Box>
              </Box>
              {placeLine ? (
                <Tooltip title={placeLine} placement="top" enterDelay={500}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={dashboard2TodayColleagueBookingRowSx(
                      DASHBOARD2_CHIP_AVATAR_SIZE_PX,
                      COLLEAGUE_AVATAR_GAP_PX
                    )}
                    data-testid={`dashboard2-today-colleague-place-${colleague.userId}`}
                  >
                    {placeLine}
                  </Typography>
                </Tooltip>
              ) : null}
            </Box>
          );
        })}
      </Box>
    );
  })();

  return (
    <Dashboard2SectionPanel
      icon={<Groups fontSize="small" />}
      title={t("_dashboard2_todayColleaguesLabel")}
      description={t("_dashboard2_todayColleaguesDescription")}
      iconSx={dashboard2SectionFieldsetLegendIconSx(theme)}
      rootSx={dashboard2TodayColleaguesSectionRootSx(isWideRow)}
      panelSx={dashboard2TodayColleaguesPanelSx(theme, isWideRow)}
      data-testid="dashboard2-today-colleagues-strip"
      headerTestId="dashboard2-today-colleagues-legend"
    >
      <Box
        sx={dashboard2TodayColleaguesScrollSx(isWideRow)}
        data-testid="dashboard2-today-colleagues-strip-scroll"
        data-layout={isWideRow ? "wide" : "narrow"}
      >
        {stripContent}
      </Box>
    </Dashboard2SectionPanel>
  );
};
